import { useEffect, useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, AlertTriangle, AlertCircle, Info, RefreshCw, Filter,
  ChevronDown, ChevronRight, Plus, X, Send, Activity, Layers,
} from 'lucide-react';
import { api } from '@/lib/api';
import { colors, getNeumorphicShadow } from '@/lib/designSystem';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';

const SEVERITY = {
  BLOCKER: { bg: '#fef2f2', text: '#991b1b', dot: '#dc2626', icon: AlertCircle, label: 'BLOCKER' },
  WARNING: { bg: '#fffbeb', text: '#92400e', dot: '#d97706', icon: AlertTriangle, label: 'WARNING' },
  INFO:    { bg: '#eff6ff', text: '#1e40af', dot: '#3b82f6', icon: Info, label: 'INFO' },
};

const formatTs = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

const GovernanceFeed = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const c = colors[theme];

  const [data, setData] = useState(null);
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ module_key: '', signal_type: '', severity: '' });
  const [expanded, setExpanded] = useState({});
  const [showTestModal, setShowTestModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const [feedRes, modRes] = await Promise.all([
        api.getGovernanceSignals(params),
        api.getGovernanceModules(),
      ]);
      setData(feedRes.data);
      setModules(modRes.data?.modules || {});
    } catch (e) {
      console.error('Error loading governance feed:', e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const signalTypeOptions = useMemo(() => {
    if (!filters.module_key) {
      const all = [];
      Object.values(modules).forEach((m) => Object.keys(m.signal_types || {}).forEach((k) => all.push(k)));
      return [...new Set(all)];
    }
    return Object.keys(modules[filters.module_key]?.signal_types || {});
  }, [filters.module_key, modules]);

  const severitySummary = data?.severity_summary || { BLOCKER: 0, WARNING: 0, INFO: 0 };
  const moduleSummary = data?.module_summary || {};
  const signals = data?.signals || [];
  const total = data?.total || 0;

  return (
    <div className="min-h-screen p-4 md:p-8" data-testid="governance-feed-page" style={{ background: c.background }}>
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3" style={{ color: c.textPrimary }}>
            <Shield size={28} style={{ color: c.accentBlue }} />
            Governance Feed
          </h1>
          <p className="text-sm mt-1" style={{ color: c.textSecondary }}>
            Operational signals from governed engines — read-only. Control Room never mutates source records.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {user?.is_org_admin && (
            <button
              onClick={() => setShowTestModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all hover:scale-[1.02]"
              style={{ background: c.cardBg, boxShadow: getNeumorphicShadow(theme), color: c.accentBlue }}
              data-testid="add-test-signal-btn"
            >
              <Plus size={16} /> Add Test Signal
            </button>
          )}
          <button
            onClick={load}
            disabled={loading}
            className="p-2.5 rounded-xl transition-all"
            style={{ background: c.cardBg, boxShadow: getNeumorphicShadow(theme), color: c.accentBlue }}
            data-testid="governance-refresh"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </motion.div>

      {/* Severity KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="severity-summary">
        <SeverityCard c={c} theme={theme} severityKey="BLOCKER" count={severitySummary.BLOCKER || 0} />
        <SeverityCard c={c} theme={theme} severityKey="WARNING" count={severitySummary.WARNING || 0} />
        <SeverityCard c={c} theme={theme} severityKey="INFO" count={severitySummary.INFO || 0} />
        <KpiCard c={c} theme={theme} label="Total Signals" value={total} icon={Activity} color={c.accentBlue} testId="kpi-total" />
      </div>

      {/* Module summary */}
      {Object.keys(moduleSummary).length > 0 && (
        <Panel c={c} theme={theme} title="Governed Modules" icon={Layers} testId="module-summary">
          <div className="flex flex-wrap gap-2">
            {Object.entries(moduleSummary).map(([modKey, count]) => {
              const label = modules[modKey]?.label || modKey;
              const active = filters.module_key === modKey;
              return (
                <button
                  key={modKey}
                  onClick={() => setFilters((f) => ({ ...f, module_key: active ? '' : modKey, signal_type: '' }))}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                  style={{
                    background: active ? c.accentBlue : c.background,
                    color: active ? '#fff' : c.textPrimary,
                    boxShadow: active ? 'none' : getNeumorphicShadow(theme),
                  }}
                  data-testid={`module-pill-${modKey}`}
                >
                  {label} <span className="opacity-70 ml-1">({count})</span>
                </button>
              );
            })}
          </div>
        </Panel>
      )}

      {/* Filters */}
      <Panel c={c} theme={theme} title="Filter" icon={Filter} testId="filters-panel">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select
            c={c} theme={theme}
            label="Module"
            value={filters.module_key}
            onChange={(v) => setFilters((f) => ({ ...f, module_key: v, signal_type: '' }))}
            options={[{ value: '', label: 'All modules' }, ...Object.entries(modules).map(([k, v]) => ({ value: k, label: v.label }))]}
            testId="filter-module"
          />
          <Select
            c={c} theme={theme}
            label="Signal Type"
            value={filters.signal_type}
            onChange={(v) => setFilters((f) => ({ ...f, signal_type: v }))}
            options={[{ value: '', label: 'All types' }, ...signalTypeOptions.map((t) => ({ value: t, label: t }))]}
            testId="filter-signal-type"
          />
          <Select
            c={c} theme={theme}
            label="Severity"
            value={filters.severity}
            onChange={(v) => setFilters((f) => ({ ...f, severity: v }))}
            options={[
              { value: '', label: 'All severities' },
              { value: 'BLOCKER', label: 'BLOCKER' },
              { value: 'WARNING', label: 'WARNING' },
              { value: 'INFO', label: 'INFO' },
            ]}
            testId="filter-severity"
          />
        </div>
      </Panel>

      {/* Feed */}
      <Panel c={c} theme={theme} title={`Signal Feed (${signals.length}${total > signals.length ? ` of ${total}` : ''})`} icon={Activity} testId="feed-panel">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: c.accentBlue }} />
          </div>
        ) : signals.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm rounded-lg" style={{ background: c.background, color: c.textSecondary }} data-testid="empty-feed">
            No signals match the current filters. As governed engines report events, they will appear here.
          </div>
        ) : (
          <div className="space-y-2" data-testid="signal-list">
            {signals.map((s) => (
              <SignalRow
                key={s.signal_id}
                c={c}
                theme={theme}
                signal={s}
                expanded={!!expanded[s.signal_id]}
                onToggle={() => setExpanded((e) => ({ ...e, [s.signal_id]: !e[s.signal_id] }))}
                moduleLabel={modules[s.module_key]?.label || s.module_key}
              />
            ))}
          </div>
        )}
      </Panel>

      {/* Test signal modal */}
      <AnimatePresence>
        {showTestModal && (
          <TestSignalModal
            c={c}
            theme={theme}
            modules={modules}
            onClose={() => setShowTestModal(false)}
            onIngested={() => { setShowTestModal(false); load(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Subcomponents ──

const KpiCard = ({ c, theme, label, value, icon: Icon, color, testId }) => (
  <div className="p-4 rounded-xl" style={{ background: c.cardBg, boxShadow: getNeumorphicShadow(theme) }} data-testid={testId}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs font-medium" style={{ color: c.textSecondary }}>{label}</span>
      <Icon size={16} style={{ color }} />
    </div>
    <div className="text-2xl font-bold" style={{ color: c.textPrimary }}>{value}</div>
  </div>
);

const SeverityCard = ({ c, theme, severityKey, count }) => {
  const sev = SEVERITY[severityKey];
  const Icon = sev.icon;
  const isActive = count > 0;
  return (
    <div
      className="p-4 rounded-xl"
      style={{ background: c.cardBg, boxShadow: getNeumorphicShadow(theme), borderLeft: `3px solid ${isActive ? sev.dot : c.border}` }}
      data-testid={`kpi-${severityKey.toLowerCase()}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium" style={{ color: c.textSecondary }}>{sev.label}</span>
        <Icon size={16} style={{ color: isActive ? sev.dot : c.textSecondary }} />
      </div>
      <div className="text-2xl font-bold" style={{ color: isActive ? sev.dot : c.textPrimary }}>{count}</div>
    </div>
  );
};

const Panel = ({ c, theme, title, icon: Icon, children, testId }) => (
  <div
    className="p-5 rounded-xl mb-6"
    style={{ background: c.cardBg, boxShadow: getNeumorphicShadow(theme) }}
    data-testid={testId}
  >
    <div className="flex items-center gap-2 mb-4">
      {Icon && <Icon size={18} style={{ color: c.accentBlue }} />}
      <h2 className="text-base font-semibold" style={{ color: c.textPrimary }}>{title}</h2>
    </div>
    {children}
  </div>
);

const Select = ({ c, theme, label, value, onChange, options, testId }) => (
  <label className="block">
    <span className="text-xs font-medium mb-1.5 block" style={{ color: c.textSecondary }}>{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 rounded-lg text-sm outline-none"
      style={{
        background: c.background,
        color: c.textPrimary,
        border: `1px solid ${c.border}`,
      }}
      data-testid={testId}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </label>
);

const SignalRow = ({ c, theme, signal, expanded, onToggle, moduleLabel }) => {
  const sev = SEVERITY[signal.severity] || SEVERITY.INFO;
  const Icon = sev.icon;
  const hasDetails = signal.details && Object.keys(signal.details).length > 0;
  return (
    <div
      className="rounded-lg overflow-hidden"
      style={{ background: c.background, border: `1px solid ${c.border}` }}
      data-testid={`signal-row-${signal.signal_id}`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:opacity-80 transition-opacity"
      >
        <span
          className="px-2 py-1 rounded-md text-[10px] font-bold flex items-center gap-1.5"
          style={{ background: sev.bg, color: sev.text }}
        >
          <Icon size={11} />
          {sev.label}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold" style={{ color: c.textPrimary }}>
              {signal.signal_label || signal.signal_type}
            </span>
            <span className="text-[11px] px-1.5 py-0.5 rounded" style={{ background: c.cardBg, color: c.textSecondary }}>
              {moduleLabel}
            </span>
            {signal.entity_type && (
              <span className="text-[11px] font-mono" style={{ color: c.textSecondary }}>
                {signal.entity_type}{signal.entity_id ? `:${signal.entity_id}` : ''}
              </span>
            )}
          </div>
          <div className="text-[11px] mt-0.5" style={{ color: c.textSecondary }}>
            {formatTs(signal.timestamp_utc)}
            {signal.outlet_id && <span className="ml-2">· outlet {signal.outlet_id}</span>}
          </div>
        </div>
        {hasDetails && (expanded ? <ChevronDown size={16} style={{ color: c.textSecondary }} /> : <ChevronRight size={16} style={{ color: c.textSecondary }} />)}
      </button>
      <AnimatePresence>
        {expanded && hasDetails && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
            data-testid={`signal-details-${signal.signal_id}`}
          >
            <div className="px-4 pb-3 pt-1 border-t" style={{ borderColor: c.border }}>
              <div className="text-[10px] uppercase font-semibold mb-1.5" style={{ color: c.textSecondary }}>
                Payload
              </div>
              <pre
                className="text-[11px] font-mono p-2 rounded overflow-x-auto"
                style={{ background: c.cardBg, color: c.textPrimary, border: `1px solid ${c.border}` }}
              >
                {JSON.stringify(signal.details, null, 2)}
              </pre>
              <div className="grid grid-cols-2 gap-2 mt-2 text-[11px]" style={{ color: c.textSecondary }}>
                <div><strong>signal_id:</strong> <span className="font-mono">{signal.signal_id}</span></div>
                {signal.source_id && <div><strong>source_id:</strong> <span className="font-mono">{signal.source_id}</span></div>}
                <div><strong>ingested_at:</strong> {formatTs(signal.ingested_at)}</div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const TestSignalModal = ({ c, theme, modules, onClose, onIngested }) => {
  const moduleKeys = Object.keys(modules);
  const [moduleKey, setModuleKey] = useState(moduleKeys[0] || 'people_engine');
  const signalTypes = Object.keys(modules[moduleKey]?.signal_types || {});
  const [signalType, setSignalType] = useState(signalTypes[0] || 'LOW_FACE_CONFIDENCE');
  const [entityId, setEntityId] = useState('test-entity-001');
  const [entityType, setEntityType] = useState('employee');
  const [outletId, setOutletId] = useState('');
  const [detailsRaw, setDetailsRaw] = useState('{\n  "test": true\n}');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset signal_type when module changes
  useEffect(() => {
    const types = Object.keys(modules[moduleKey]?.signal_types || {});
    if (types.length > 0 && !types.includes(signalType)) setSignalType(types[0]);
  }, [moduleKey, modules, signalType]);

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    let details = {};
    try {
      details = detailsRaw.trim() ? JSON.parse(detailsRaw) : {};
    } catch {
      setError('Details must be valid JSON.');
      setSubmitting(false);
      return;
    }
    try {
      await api.ingestGovernanceSignal({
        module_key: moduleKey,
        signal_type: signalType,
        entity_id: entityId || null,
        entity_type: entityType || null,
        outlet_id: outletId || null,
        details,
      });
      onIngested();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to ingest signal');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid="test-signal-modal"
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.96, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl p-6"
        style={{ background: c.cardBg, boxShadow: getNeumorphicShadow(theme) }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2" style={{ color: c.textPrimary }}>
            <Send size={18} style={{ color: c.accentBlue }} />
            Ingest Test Signal
          </h3>
          <button onClick={onClose} style={{ color: c.textSecondary }}>
            <X size={18} />
          </button>
        </div>

        <p className="text-xs mb-4" style={{ color: c.textSecondary }}>
          Simulates an upstream engine emitting a governance signal. Severity is auto-classified by the registry.
        </p>

        <div className="space-y-3">
          <Select
            c={c} theme={theme}
            label="Module"
            value={moduleKey}
            onChange={setModuleKey}
            options={moduleKeys.map((k) => ({ value: k, label: modules[k].label }))}
            testId="test-module"
          />
          <Select
            c={c} theme={theme}
            label="Signal Type"
            value={signalType}
            onChange={setSignalType}
            options={signalTypes.map((t) => ({ value: t, label: `${t} (${modules[moduleKey]?.signal_types?.[t]?.severity})` }))}
            testId="test-signal-type"
          />
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-xs font-medium mb-1.5 block" style={{ color: c.textSecondary }}>Entity Type</span>
              <input
                value={entityType}
                onChange={(e) => setEntityType(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                style={{ background: c.background, color: c.textPrimary, border: `1px solid ${c.border}` }}
                data-testid="test-entity-type"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium mb-1.5 block" style={{ color: c.textSecondary }}>Entity ID</span>
              <input
                value={entityId}
                onChange={(e) => setEntityId(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                style={{ background: c.background, color: c.textPrimary, border: `1px solid ${c.border}` }}
                data-testid="test-entity-id"
              />
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-medium mb-1.5 block" style={{ color: c.textSecondary }}>Outlet ID (optional)</span>
            <input
              value={outletId}
              onChange={(e) => setOutletId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm outline-none"
              style={{ background: c.background, color: c.textPrimary, border: `1px solid ${c.border}` }}
              data-testid="test-outlet-id"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium mb-1.5 block" style={{ color: c.textSecondary }}>Details (JSON)</span>
            <textarea
              value={detailsRaw}
              onChange={(e) => setDetailsRaw(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 rounded-lg text-xs font-mono outline-none"
              style={{ background: c.background, color: c.textPrimary, border: `1px solid ${c.border}` }}
              data-testid="test-details"
            />
          </label>
        </div>

        {error && (
          <div className="mt-3 px-3 py-2 rounded-lg text-xs" style={{ background: '#fef2f2', color: '#991b1b' }} data-testid="test-error">
            {error}
          </div>
        )}

        <div className="flex gap-2 mt-5">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 rounded-lg text-sm font-medium"
            style={{ background: c.background, color: c.textPrimary, border: `1px solid ${c.border}` }}
            data-testid="test-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="flex-1 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60"
            style={{ background: c.accentBlue }}
            data-testid="test-submit"
          >
            {submitting ? 'Ingesting…' : 'Ingest Signal'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default GovernanceFeed;
